/**
 * Proforma Export Utilities
 *
 * This module provides the data extraction and JSON-building logic for the
 * "Export Proforma JSON" feature. It assembles a structured JSON object from
 * the patient's dataModel, containing clinician decisions and notes from
 * assessment modules (currently Diagnostic Decision and Post-Assessment
 * Discussion).
 *
 * DESIGN DECISIONS:
 * -----------------
 * 1. CLIENT-SIDE ONLY: Unlike the existing PDF/XLS export (which calls a
 *    Firebase Callable Function for server-side rendering), this feature
 *    operates entirely client-side. The patientDataModel already contains
 *    all required data in memory, so no additional network requests are needed.
 *
 * 2. DEEP-CLONE + STRIP: Rather than mapping individual fields one-by-one
 *    (which is fragile and requires updates whenever a module adds new fields),
 *    we deep-clone the relevant dataModel subtree and strip internal metadata
 *    fields. This ensures future fields are automatically included.
 *
 * 3. TIMESTAMP SERIALIZATION: Firestore Timestamp objects (both live instances
 *    with a `toDate()` method AND deserialized plain objects with `seconds`/
 *    `nanoseconds` from localStorage rehydration) are recursively converted
 *    to ISO 8601 strings for portable, human-readable JSON output.
 *
 * 4. DATA MINIMIZATION: Only the selected module subtrees are included in the
 *    export. Top-level patient PII (email, etc.) is excluded. The only
 *    patient-identifying info is the patient ID and name (for audit context).
 */

// Fields to strip from exported data — these are internal platform metadata
// that have no clinical relevance and would clutter the export.
const METADATA_FIELDS_TO_STRIP = [
  'stageNumber',
  'progress',
  'docs_available',
  'status',
  'currentSection',
  'lastVisitedSection',
];

/**
 * Recursively serializes Firestore Timestamp objects to ISO 8601 strings.
 *
 * Handles two representations that appear in patientDataModel:
 * - Live Firestore Timestamp instances: objects with a `toDate()` method
 *   (present when the data was just fetched from Firestore).
 * - Serialized timestamp objects: plain objects with `seconds` and `nanoseconds`
 *   properties (present after JSON.parse from localStorage, where `toDate()` is
 *   lost during serialization).
 *
 * Both are converted to ISO 8601 format (e.g. "2026-03-15T14:30:00.000Z") for
 * consistent, human-readable JSON output that's easily parseable by any system.
 *
 * @param {any} value - The value to process (recursively walks objects/arrays)
 * @returns {any} The value with all timestamps converted to ISO strings
 */
export const serializeTimestamps = (value) => {
  if (value === null || value === undefined) return value;

  // Live Firestore Timestamp with toDate() method
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    try {
      return value.toDate().toISOString();
    } catch {
      return null;
    }
  }

  // Serialized timestamp object from localStorage (has seconds but no toDate)
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    typeof (value.seconds ?? value._seconds) === 'number' &&
    Object.keys(value).length <= 2
  ) {
    const seconds = value.seconds ?? value._seconds;
    try {
      return new Date(seconds * 1000).toISOString();
    } catch {
      return null;
    }
  }

  // Recurse into arrays
  if (Array.isArray(value)) {
    return value.map(serializeTimestamps);
  }

  // Recurse into plain objects
  if (typeof value === 'object') {
    const result = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = serializeTimestamps(val);
    }
    return result;
  }

  // Primitives pass through unchanged
  return value;
};

/**
 * Deep-clones a data subtree, stripping internal metadata fields and
 * serializing all Timestamp objects to ISO strings.
 *
 * This is the core transformation applied to each module's dataModel
 * subtree before inclusion in the export JSON.
 *
 * @param {Object} data - The raw dataModel subtree to process
 * @returns {Object|null} Cleaned data ready for export, or null if input is empty
 */
export const cleanAndSerialize = (data) => {
  if (!data || typeof data !== 'object') return null;

  // Deep clone to avoid mutating the original patientDataModel in memory
  const cloned = JSON.parse(JSON.stringify(data));

  // Strip metadata fields recursively
  const stripMetadata = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) {
      return obj.map(stripMetadata);
    }

    const cleaned = {};
    for (const [key, val] of Object.entries(obj)) {
      if (METADATA_FIELDS_TO_STRIP.includes(key)) continue;
      cleaned[key] = typeof val === 'object' ? stripMetadata(val) : val;
    }
    return cleaned;
  };

  const stripped = stripMetadata(cloned);

  // JSON.parse + stringify in the clone step already loses toDate() methods,
  // but we still need to convert {seconds, nanoseconds} objects to ISO strings.
  return serializeTimestamps(stripped);
};

/**
 * Extracts the Diagnostic Decision (DD) module data for export.
 *
 * The DD module lives at `dataModel['4_diagnosticDecision']` and contains:
 * - Section 1: Primary diagnosis decision (ADHD criteria, decision, severity,
 *   presentation, clinician notes for "not met" next steps)
 * - Section 2: Comorbid Secondary Diagnoses (map of condition → {decision, notes})
 *
 * Note: Section 3 (Formulation) is TOC-only and excluded for Well Life Clinic.
 *
 * @param {Object} patientDataModel - The full patient data model from context
 * @returns {Object|null} Extracted DD data, or null if module has no data
 */
export const extractDiagnosticDecisionData = (patientDataModel) => {
  const ddModule = patientDataModel?.dataModel?.['4_diagnosticDecision'];
  if (!ddModule || !ddModule.sections) return null;

  const sections = ddModule.sections;
  const result = {};

  // Section 1: Primary ADHD Diagnostic Decision
  if (sections['1']) {
    result.primaryDiagnosis = cleanAndSerialize(sections['1']);
  }

  // Section 2: Comorbid Secondary Diagnoses
  if (sections['2']) {
    result.comorbidSecondaryDiagnoses = cleanAndSerialize(sections['2']);
  }

  // Return null if there's genuinely no data in any section
  if (Object.keys(result).length === 0) return null;

  return result;
};

/**
 * Extracts the Post-Assessment Discussion (PAD) module data for export.
 *
 * The PAD module lives at `dataModel['5_postAssessmentDiscussion']` and contains:
 * - Section 1 (REV): Review of Assessment — clinician notes and discussion answers
 * - Section 2 (DIS): Strategies and Recommendations — per-subsection answers and notes
 * - Section 3 (TRT): Treatment Options — selections with otherValue
 * - Section 4 (HLT): Health Checks — selections, otherValue, and vital snapshots
 * - Section 5 (AMP): Agreed Medication Plan — medication details and informed consent
 *
 * @param {Object} patientDataModel - The full patient data model from context
 * @returns {Object|null} Extracted PAD data, or null if module has no data
 */
export const extractPostAssessmentDiscussionData = (patientDataModel) => {
  const padModule = patientDataModel?.dataModel?.['5_postAssessmentDiscussion'];
  if (!padModule || !padModule.sections) return null;

  const sections = padModule.sections;
  const result = {};

  // Section 1: Review of Assessment
  if (sections['1']) {
    result.reviewOfAssessment = cleanAndSerialize(sections['1']);
  }

  // Section 2: Strategies and Recommendations
  if (sections['2']) {
    result.strategiesAndRecommendations = cleanAndSerialize(sections['2']);
  }

  // Section 3: Treatment Options
  if (sections['3']) {
    result.treatmentOptions = cleanAndSerialize(sections['3']);
  }

  // Section 4: Health Checks (includes vital snapshots like pad_height, pad_weight, etc.)
  if (sections['4']) {
    result.healthChecks = cleanAndSerialize(sections['4']);
  }

  // Section 5: Agreed Medication Plan
  if (sections['5']) {
    result.agreedMedicationPlan = cleanAndSerialize(sections['5']);
  }

  // Return null if there's genuinely no data in any section
  if (Object.keys(result).length === 0) return null;

  return result;
};

/**
 * Checks whether a given module has any substantive data in the patient's
 * dataModel. Used to enable/disable module checkboxes in the export dialog —
 * modules with no data are disabled to prevent exporting empty stubs.
 *
 * @param {Object} patientDataModel - The full patient data model from context
 * @param {string} moduleKey - The module identifier ('dd' or 'pad')
 * @returns {boolean} True if the module has at least some data
 */
export const moduleHasData = (patientDataModel, moduleKey) => {
  switch (moduleKey) {
    case 'dd': {
      const ddModule = patientDataModel?.dataModel?.['4_diagnosticDecision'];
      return !!(ddModule && ddModule.sections && Object.keys(ddModule.sections).length > 0);
    }
    case 'pad': {
      const padModule = patientDataModel?.dataModel?.['5_postAssessmentDiscussion'];
      return !!(padModule && padModule.sections && Object.keys(padModule.sections).length > 0);
    }
    default:
      return false;
  }
};

/**
 * Builds the complete proforma export JSON object from the patient's data,
 * filtered by the user's module selection.
 *
 * The output structure is:
 * {
 *   exportMetadata: { exportedAt, exportVersion, patientId, patientName },
 *   modules: {
 *     diagnosticDecision: { ... } | null,
 *     postAssessmentDiscussion: { ... } | null
 *   }
 * }
 *
 * @param {Object} patientDataModel - The full patient data model from context
 * @param {Object} selectedModules - Map of module keys to booleans, e.g. { dd: true, pad: true }
 * @returns {Object} The complete JSON object ready for download
 */
export const buildProformaExportJSON = (patientDataModel, selectedModules) => {
  const patientName = [patientDataModel?.firstName, patientDataModel?.lastName]
    .filter(Boolean)
    .join(' ');

  const exportPayload = {
    exportMetadata: {
      exportedAt: new Date().toISOString(),
      exportVersion: '1.0',
      patientId: patientDataModel?.patientId || null,
      patientName: patientName || null,
    },
    modules: {},
  };

  if (selectedModules.dd) {
    exportPayload.modules.diagnosticDecision = extractDiagnosticDecisionData(patientDataModel);
  }

  if (selectedModules.pad) {
    exportPayload.modules.postAssessmentDiscussion = extractPostAssessmentDiscussionData(patientDataModel);
  }

  return exportPayload;
};

/**
 * Generates a timestamped filename for the proforma JSON export.
 * Format: "{FirstName}_{LastName}_proforma_{YYYY-MM-DD}.json"
 *
 * @param {Object} patientDataModel - The patient data model (for name)
 * @returns {string} The generated filename
 */
export const generateProformaFilename = (patientDataModel) => {
  const firstName = (patientDataModel?.firstName || 'Unknown').replace(/[^a-zA-Z0-9]/g, '');
  const lastName = (patientDataModel?.lastName || 'Patient').replace(/[^a-zA-Z0-9]/g, '');
  const dateStr = new Date().toISOString().split('T')[0];
  return `${firstName}_${lastName}_proforma_${dateStr}.json`;
};

/**
 * Triggers a browser download of the JSON payload as a file.
 * Creates a temporary Blob URL, programmatically clicks a hidden anchor,
 * and then revokes the URL to free memory.
 *
 * @param {Object} jsonPayload - The JSON object to download
 * @param {string} filename - The filename for the downloaded file
 */
export const downloadJSON = (jsonPayload, filename) => {
  const jsonString = JSON.stringify(jsonPayload, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();

  // Cleanup: remove anchor and revoke object URL to prevent memory leaks
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
